import type { SiteProgress, Uuid } from '@/lib/types';

/*
  ════════════════════════════════════════════════════════════════════════════
   공정율을 접는 규칙. **backend 의 `WbsService.rollup` 과 같은 식이다.**
  ════════════════════════════════════════════════════════════════════════════

  서버가 접어 준 값을 받는 편이 나았겠지만 `/api/external/**` 의 공정 응답은 줄만 준다
  (`SiteProgressRow`). 그래서 여기서 접되, 규칙을 **그대로** 옮긴다 — 같은 현장인데 내부 화면과
  포털의 공정율이 다르면 어느 쪽이 맞는지 아무도 모른다.

  식은 Σ(weight × rate) ÷ Σ(weight) 이고, 분모에 들어가는 줄이 요점이다:

    1. **리프만 센다.** 부모 공정은 자식의 합이라, 함께 세면 같은 일을 두 번 센다(V27:87).
    2. **「소계」·「계」 줄을 뺀다.** 도급내역서를 엑셀로 읽어 오면 이런 줄이 그대로 공정이 되고,
       자식이 없어 1 번을 통과한다. 실측(청주 반도체 공장, 2026-09-01)에서 총액이 **2.37배**로
       부풀었다 — 뺄 이유가 짐작이 아니라 잰 값이다(`WbsService.isAggregateRow`).
    3. 실적이 아직 없는 리프는 **분모에 남고 분자에 0** 으로 들어간다. 안 잰 공정을 빼면
       공정율이 시작하자마자 100% 에 가까워진다.
*/

/**
 * 합계 줄인가.
 *
 * <p>「소계」·「합계」·「총계」·「누계」는 어디에 있든 합계다. 「계」 한 글자는 <b>이름 끝에 홀로
 * 있을 때만</b> 본다 — 그러지 않으면 「6. 기<b>계</b>공사」 같은 진짜 공종이 함께 지워진다.
 * 전각 공백(U+3000)을 넣는 것은 한글 문서에서 「소」와 「계」를 벌려 쓴 것이 흔해서다.
 * 그 글자를 소스에 그대로 두지 않고 `\u3000` 으로 적는 것은, 눈에 보이지 않는 문자가 코드에
 * 섞이면 나중에 지웠는지 아닌지를 아무도 확신하지 못하기 때문이다(`no-irregular-whitespace`).
 */
const AGGREGATE_NAME =
  /[소합총누][\s\u3000]*계|(?:^|[\s\u3000\])])계[\s\u3000]*[\])]?[\s\u3000]*$|\b(?:sub)?\s*total\b/i;

function isAggregate(row: SiteProgress): boolean {
  return AGGREGATE_NAME.test(row.name);
}

/** 자식을 가진 공정의 id. 여기 든 줄은 「일」이 아니라 묶음이다. */
function parentIds(rows: SiteProgress[]): Set<Uuid> {
  const ids = new Set<Uuid>();
  for (const row of rows) {
    if (row.parentId !== null) ids.add(row.parentId);
  }
  return ids;
}

/** 계산에 쓰는 말단 공정 — 부모가 아니고 합계 줄도 아닌 것. */
export function countableLeaves(rows: SiteProgress[]): SiteProgress[] {
  const parents = parentIds(rows);
  return rows.filter((row) => !parents.has(row.id) && !isAggregate(row));
}

/** 계획보다 실적이 뒤진 공정. 둘 다 있는 줄만 견준다 — 안 잰 것은 늦은 것이 아니다. */
export function isBehind(row: SiteProgress): boolean {
  return row.plannedRate !== null && row.actualRate !== null && row.actualRate < row.plannedRate;
}

export type ProgressRollup = {
  /** 접힌 값이 없으면 null 이다. 0% 와 「아직 없음」은 다른 상태다. */
  plannedRate: number | null;
  actualRate: number | null;
  /** 센 공정 수. 0 이면 접을 것이 없었다. */
  countedItems: number;
  /** 계획보다 뒤진 공정 수. 리프만 센다 — 부모까지 세면 한 지연이 여러 번 셈해진다. */
  behindCount: number;
};

const EMPTY: ProgressRollup = { plannedRate: null, actualRate: null, countedItems: 0, behindCount: 0 };

/** 현장 전체 공정율. 규칙은 이 파일 머리에 있다. */
export function rollupProgress(rows: SiteProgress[]): ProgressRollup {
  const leaves = countableLeaves(rows);

  let weightSum = 0;
  let plannedSum = 0;
  let actualSum = 0;
  let counted = 0;

  for (const row of leaves) {
    const weight = row.weight ?? 0;
    weightSum += weight;
    // 실적이 한 번도 안 들어온 공정은 분모에만 남는다.
    if (row.recordDate === null) continue;
    counted += 1;
    plannedSum += weight * (row.plannedRate ?? 0);
    actualSum += weight * (row.actualRate ?? 0);
  }

  if (counted === 0 || weightSum === 0) return EMPTY;

  return {
    plannedRate: plannedSum / weightSum,
    actualRate: actualSum / weightSum,
    countedItems: counted,
    behindCount: leaves.filter(isBehind).length,
  };
}

/** 나무를 편 한 줄. `depth` 는 들여쓰기에만 쓴다. */
export type ProgressNode = { row: SiteProgress; depth: number; leaf: boolean };

/**
 * 공정 나무를 <b>보이는 순서대로</b> 편다.
 *
 * <p>서버가 이미 `sort_order`·`code` 로 세워 준 줄을 부모 아래로 옮겨 담을 뿐이라, 형제 사이의
 * 순서는 그대로다. 뿌리를 잃은 줄(부모가 목록 밖)은 버리지 않고 최상위로 올린다 —
 * 실제로 `parent_id` 는 부모가 지워지면 NULL 이 되지만(V27:28), 그 사이 상태에서 줄이 통째로
 * 사라지면 공정표에 구멍이 뚫린 것처럼 보인다.
 */
export function flattenProgress(rows: SiteProgress[]): ProgressNode[] {
  const byParent = new Map<Uuid | null, SiteProgress[]>();
  const known = new Set(rows.map((row) => row.id));

  for (const row of rows) {
    const key = row.parentId !== null && known.has(row.parentId) ? row.parentId : null;
    const bucket = byParent.get(key);
    if (bucket === undefined) byParent.set(key, [row]);
    else bucket.push(row);
  }

  const out: ProgressNode[] = [];
  const walk = (parent: Uuid | null, depth: number) => {
    for (const row of byParent.get(parent) ?? []) {
      const children = byParent.get(row.id) ?? [];
      out.push({ row, depth, leaf: children.length === 0 });
      walk(row.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
